import { useRef, useState } from 'react';
import { useStyletron } from 'baseui';
import { PLACEMENT, Popover } from 'baseui/popover';

import ENGTImage from 'components/UI/ENGTImage/ENGTImage';
import ENGTInput, { IENGTInputProps } from 'components/UI/ENGTInput/ENGTInput';
import NewPasswordWidget from 'components/UI/NewPasswordWidget/NewPasswordWidget';
import LoginInputError from 'components/UI/Validation/LoginInputError';

import { getPasswordRules } from 'shared/helpers';
import RegistrationPasswordSuccessCheck from 'shared/icons/RegistrationPasswordSuccessCheck.svg';

import PasswordWidget from './PasswordWidget';

export interface IENGTPasswordProps {
	isPopverWidget?: boolean;
	isPasswordWidgetOpen?: boolean;
	isNewPasswordWidgetOpen?: boolean;
	showErrorMessage?: boolean;
	validPassword?: boolean;
	errorText?: string;
	password?: any;
}

function ENGTPassword(props: IENGTPasswordProps & IENGTInputProps) {
	const [css] = useStyletron();
	const wrapperDiv = useRef<HTMLDivElement>(null);
	const [position, setposition] = useState('topLeft');

	const {
		isPopverWidget = false,
		isPasswordWidgetOpen = false,
		isNewPasswordWidgetOpen = false,
		password = '',
		showErrorMessage = false,
		validPassword = false,
		errorText = '',
	} = props;
	const passwordRules = getPasswordRules(password);
	const isPasswordTooltipValid = !!password && passwordRules.isValidLength && passwordRules.hasNonSpaceCharacter;

	const iconCss: any = {
		top: '1rem',
		position: 'absolute',
		display: 'inline-block',
	};

	const renderPasswordStatus = () => {
		let content = null;

		if (isNewPasswordWidgetOpen && !isPasswordTooltipValid) {
			content = <NewPasswordWidget value={password} />;
		} else if (isNewPasswordWidgetOpen && isPasswordTooltipValid) {
			content = (
				<div className={css({ height: '1.125rem' })}>
					<ENGTImage lazy src={RegistrationPasswordSuccessCheck} width={21} />
				</div>
			);
		} else if (validPassword) {
			content = (
				<div className={css({ height: '1.125rem' })}>
					<ENGTImage lazy src={RegistrationPasswordSuccessCheck} width={21} />
				</div>
			);
		} else if (showErrorMessage) {
			content = <LoginInputError text={errorText} />;
		}

		if (!content) {
			return null;
		}

		return (
			<div
				className={css({
					left: '100%',
					...iconCss,
				})}
			>
				{content}
			</div>
		);
	};

	if (isPopverWidget) {
		return (
			<div ref={wrapperDiv}>
				<Popover
					isOpen={isPasswordWidgetOpen && !isPasswordTooltipValid}
					content={
						<PasswordWidget
							value={password}
							wrapperCss={{
								marginBottom: 0,
								borderRadius: position === 'topLeft' ? '0.5rem 0.5rem 0 0' : '0 0 0.5rem 0.5rem',
							}}
						/>
					}
					placement={PLACEMENT.bottomLeft}
					overrides={{
						Body: {
							style: ({ $placement }) => {
								if ($placement !== position) {
									setposition($placement || 'topLeft');
								}

								return {
									width: `${wrapperDiv.current?.clientWidth}px`,
									boxShadow: '0',
								};
							},
						},
					}}
					popoverMargin={0}
					renderAll
					ignoreBoundary
				>
					<div className={css({ position: 'relative', display: 'inline-block', width: '100%' })}>
						<ENGTInput type='password' isPasswordWidgetOpen={isNewPasswordWidgetOpen} {...props} />
					</div>
					{renderPasswordStatus()}
				</Popover>
			</div>
		);
	}

	return (
		<>
			<div className={css({ position: 'relative', display: 'inline-block', width: '100%' })}>
				<ENGTInput type='password' isPasswordWidgetOpen={isNewPasswordWidgetOpen} {...props} />
			</div>
			{renderPasswordStatus()}

			{isPasswordWidgetOpen && !isPasswordTooltipValid && <PasswordWidget value={password} />}
		</>
	);
}

export default ENGTPassword;
